import React from "react";

import {
    SafetyCertificateOutlined,
    ArrowLeftOutlined,
    TeamOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    TagOutlined,
    CheckCircleFilled,
    UserOutlined,
    CrownOutlined,
    CalendarOutlined,
    SwapOutlined,
    LockOutlined,
} from "@ant-design/icons";
import {
    Card,
    Button,
    Typography,
    Row,
    Col,
    Divider,
    Space,
    Avatar,
    Tag,
    theme,
    Input,
    Alert,
} from "antd";
import QurealSection from "../../common/QurealSection";
import SectionHeader from "../../components/commons/SectionHeader";
import PricingModal from "../../components/PricingModal";

const { Title, Text } = Typography;
const { useToken } = theme;

const BillingReviewSection = ({
    plan,
    organization,
    user,
    onBack,
    onProceed,
    loading,
    paymentType = "new",
    onApplyCoupon,
    onRemoveCoupon,
    previewData,
    applyingCoupon,
    couponError,
    paymentCancelled,
    onChangePlan,
}) => {
    const { token } = useToken();
    const [couponCode, setCouponCode] = React.useState("");
    const [localError, setLocalError] = React.useState("");
    const [changePlanOpen, setChangePlanOpen] = React.useState(false);
    const [currentPlan, setCurrentPlan] = React.useState(plan);

    React.useEffect(() => {
        setCurrentPlan(plan);
    }, [plan]);

    if (!currentPlan) return null;

    const handleApply = () => {
        if (!couponCode.trim()) {
            setLocalError("Please enter a coupon code");
            return;
        }
        setLocalError("");
        onApplyCoupon(couponCode, () => setCouponCode(""));
    };

    const isTrial = paymentType === "trial";
    const finalPrice = isTrial
        ? "₹0"
        : `₹${previewData ? previewData.pay_now_price : currentPlan.price}`;
    const hasDiscount = !isTrial && previewData?.discount_total > 0;
    const billingCycle = currentPlan.billing_cycle_month;
    const cycleLabel = billingCycle === 1 ? "month" : "months";
    const rowStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    };
    const cardStyle = {
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: "none",
    };

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading={"Review Payment"}
                    icon={<SafetyCertificateOutlined />}
                    footer={false}
                    style={{ margin: "5px" }}
                />
            }
            wrapper_style={{}}
            footer={false}
        >
            <div style={{ padding: "20px 24px" }}>
                {paymentCancelled && (
                    <Alert
                        message="Payment was not completed. Please try again."
                        type="warning"
                        showIcon
                        closable
                        style={{
                            marginBottom: 20,
                            borderRadius: token.borderRadiusLG,
                        }}
                    />
                )}

                <Row gutter={[24, 24]}>
                    {/* LEFT COLUMN */}
                    <Col xs={24} lg={14}>
                        <Space
                            direction="vertical"
                            size={16}
                            style={{ width: "100%" }}
                        >
                            {/* Plan Card */}
                            <Card
                                bordered={false}
                                style={{
                                    ...cardStyle,
                                    borderLeft: `3px solid ${token.colorPrimary}`,
                                    background: token.colorBgContainer,
                                }}
                                styles={{ body: { padding: "20px 24px" } }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 16,
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.8px",
                                                fontWeight: 600,
                                                display: "block",
                                                marginBottom: 8,
                                            }}
                                        >
                                            Selected Plan
                                        </Text>

                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                marginBottom: 12,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Title
                                                level={3}
                                                style={{
                                                    margin: 0,
                                                    color: token.colorTextHeading,
                                                }}
                                            >
                                                {currentPlan.plan_name}
                                            </Title>
                                            {isTrial && (
                                                <Tag
                                                    color="success"
                                                    icon={<CheckCircleFilled />}
                                                >
                                                    Free Trial
                                                </Tag>
                                            )}
                                        </div>

                                        <Space size={8} wrap>
                                            <Tag
                                                icon={<CalendarOutlined />}
                                                color="blue"
                                            >
                                                {billingCycle} {cycleLabel}
                                            </Tag>
                                            <Tag
                                                icon={<CrownOutlined />}
                                                color="gold"
                                            >
                                                ₹{currentPlan.price}
                                            </Tag>
                                        </Space>

                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 13,
                                                display: "block",
                                                marginTop: 14,
                                            }}
                                        >
                                            Pay only{" "}
                                            <Text
                                                strong
                                                style={{
                                                    color: token.colorPrimary,
                                                }}
                                            >
                                                ₹{currentPlan.price}
                                            </Text>{" "}
                                            for{" "}
                                            <Text strong>
                                                {billingCycle} {cycleLabel}
                                            </Text>{" "}
                                            of full access
                                        </Text>
                                    </div>

                                    <Button
                                        size="small"
                                        icon={<SwapOutlined />}
                                        onClick={() => setChangePlanOpen(true)}
                                        style={{ flexShrink: 0, marginTop: 2 }}
                                    >
                                        Change
                                    </Button>
                                </div>
                            </Card>

                            {/* Billing Account Card */}
                            <Card
                                bordered={false}
                                style={{
                                    ...cardStyle,
                                    background: token.colorBgContainer,
                                }}
                                styles={{ body: { padding: "20px 24px" } }}
                            >
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                        fontWeight: 600,
                                        display: "block",
                                        marginBottom: 14,
                                    }}
                                >
                                    Billing Account
                                </Text>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 14,
                                    }}
                                >
                                    <Avatar
                                        size={42}
                                        icon={<TeamOutlined />}
                                        style={{
                                            backgroundColor:
                                                token.colorPrimaryBg,
                                            color: token.colorPrimary,
                                            border: `1px solid ${token.colorPrimaryBorder}`,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <Text
                                            strong
                                            style={{
                                                fontSize: 15,
                                                display: "block",
                                            }}
                                            ellipsis
                                        >
                                            {organization?.name ||
                                                "Organization Name"}
                                        </Text>
                                        <Space
                                            size={4}
                                            style={{ marginTop: 2 }}
                                        >
                                            <UserOutlined
                                                style={{
                                                    fontSize: 11,
                                                    color: token.colorTextTertiary,
                                                }}
                                            />
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                {user?.name ||
                                                    user?.email ||
                                                    `User ID: ${
                                                        user?.id || "N/A"
                                                    }`}
                                            </Text>
                                        </Space>
                                    </div>
                                </div>
                            </Card>

                            <Button
                                type="text"
                                onClick={onBack}
                                icon={<ArrowLeftOutlined />}
                                style={{
                                    color: token.colorTextSecondary,
                                    paddingLeft: 0,
                                }}
                            >
                                Back
                            </Button>
                        </Space>
                    </Col>

                    {/* RIGHT COLUMN: Order Summary */}
                    <Col xs={24} lg={10}>
                        <Card
                            bordered={false}
                            style={{
                                ...cardStyle,
                                background: token.colorBgContainer,
                                position: "sticky",
                                top: 16,
                            }}
                            styles={{ body: { padding: "24px" } }}
                        >
                            <Space
                                direction="vertical"
                                size={20}
                                style={{ width: "100%" }}
                            >
                                {/* Header */}
                                <div>
                                    <Title
                                        level={4}
                                        style={{ margin: "0 0 4px" }}
                                    >
                                        Order Summary
                                    </Title>
                                    <Space size={4}>
                                        <ClockCircleOutlined
                                            style={{
                                                fontSize: 12,
                                                color: token.colorTextTertiary,
                                            }}
                                        />
                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 13 }}
                                        >
                                            {isTrial
                                                ? "Review your trial details"
                                                : "Review your order details"}
                                        </Text>
                                    </Space>
                                </div>

                                <Divider style={{ margin: 0 }} />

                                {/* Price Breakdown */}
                                <Space
                                    direction="vertical"
                                    size={10}
                                    style={{ width: "100%" }}
                                >
                                    <div style={rowStyle}>
                                        <Text style={{ fontSize: 14 }}>
                                            {currentPlan.plan_name}
                                        </Text>
                                        <Text strong>₹{currentPlan.price}</Text>
                                    </div>
                                    <div style={rowStyle}>
                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 13 }}
                                        >
                                            Duration
                                        </Text>
                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 13 }}
                                        >
                                            {billingCycle} {cycleLabel}
                                        </Text>
                                    </div>
                                    <Divider
                                        dashed
                                        style={{ margin: "4px 0" }}
                                    />
                                    <div style={rowStyle}>
                                        <Text type="secondary">Subtotal</Text>
                                        <Text>₹{currentPlan.price}</Text>
                                    </div>
                                </Space>

                                {/* Coupon Section */}
                                <div>
                                    {previewData?.accepted_coupon_codes
                                        ?.length > 0 && (
                                        <div
                                            style={{
                                                marginBottom: 10,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 6,
                                            }}
                                        >
                                            {previewData.accepted_coupon_codes.map(
                                                (code) => (
                                                    <div
                                                        key={code}
                                                        style={{
                                                            background:
                                                                token.colorSuccessBg,
                                                            border: `1px solid ${token.colorSuccessBorder}`,
                                                            padding:
                                                                "3px 10px 3px 8px",
                                                            borderRadius:
                                                                token.borderRadius,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 6,
                                                        }}
                                                    >
                                                        <TagOutlined
                                                            style={{
                                                                color: token.colorSuccess,
                                                                fontSize: 12,
                                                            }}
                                                        />
                                                        <Text
                                                            style={{
                                                                color: token.colorSuccess,
                                                                fontWeight: 600,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            {code}
                                                        </Text>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            style={{
                                                                height: "auto",
                                                                padding:
                                                                    "0 0 0 2px",
                                                                color: token.colorTextTertiary,
                                                                lineHeight: 1,
                                                            }}
                                                            icon={
                                                                <CloseCircleOutlined
                                                                    style={{
                                                                        fontSize: 13,
                                                                    }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                onRemoveCoupon(
                                                                    code
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}

                                    <Space.Compact style={{ width: "100%" }}>
                                        <Input
                                            placeholder="Have a coupon code?"
                                            prefix={
                                                <TagOutlined
                                                    style={{
                                                        color: token.colorTextTertiary,
                                                    }}
                                                />
                                            }
                                            value={couponCode}
                                            onChange={(e) => {
                                                setCouponCode(e.target.value);
                                                if (localError)
                                                    setLocalError("");
                                            }}
                                            onPressEnter={handleApply}
                                            status={
                                                couponError || localError
                                                    ? "error"
                                                    : ""
                                            }
                                        />
                                        <Button
                                            type="primary"
                                            ghost
                                            onClick={handleApply}
                                            loading={applyingCoupon}
                                        >
                                            Apply
                                        </Button>
                                    </Space.Compact>

                                    {(couponError || localError) && (
                                        <Text
                                            type="danger"
                                            style={{
                                                fontSize: 12,
                                                marginTop: 4,
                                                display: "block",
                                            }}
                                        >
                                            {couponError || localError}
                                        </Text>
                                    )}
                                </div>

                                {/* Discount row */}
                                {hasDiscount && (
                                    <div
                                        style={{
                                            ...rowStyle,
                                            background: token.colorSuccessBg,
                                            border: `1px solid ${token.colorSuccessBorder}`,
                                            borderRadius: token.borderRadius,
                                            padding: "8px 12px",
                                        }}
                                    >
                                        <Space size={6}>
                                            <CheckCircleFilled
                                                style={{
                                                    color: token.colorSuccess,
                                                }}
                                            />
                                            <Text
                                                style={{
                                                    color: token.colorSuccess,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Discount Applied
                                            </Text>
                                        </Space>
                                        <Text
                                            style={{
                                                color: token.colorSuccess,
                                                fontWeight: 700,
                                            }}
                                        >
                                            − ₹{previewData.discount_total}
                                        </Text>
                                    </div>
                                )}

                                {/* Total */}
                                <div
                                    style={{
                                        ...rowStyle,
                                        padding: "16px 18px",
                                        background: token.colorFillAlter,
                                        borderRadius: token.borderRadiusLG,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                    }}
                                >
                                    <div>
                                        <Text
                                            strong
                                            style={{
                                                fontSize: 15,
                                                display: "block",
                                            }}
                                        >
                                            Total Amount
                                        </Text>
                                        {isTrial && (
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                Due today
                                            </Text>
                                        )}
                                        {hasDiscount && (
                                            <Text
                                                delete
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                ₹{currentPlan.price}
                                            </Text>
                                        )}
                                    </div>
                                    <Title
                                        level={2}
                                        style={{
                                            margin: 0,
                                            color: token.colorPrimary,
                                        }}
                                    >
                                        {finalPrice}
                                    </Title>
                                </div>

                                {/* CTA */}
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    loading={loading}
                                    onClick={onProceed}
                                    icon={<LockOutlined />}
                                    style={{
                                        height: 48,
                                        fontSize: 15,
                                        fontWeight: 600,
                                    }}
                                >
                                    {isTrial
                                        ? "Start Free Trial"
                                        : "Proceed to Pay"}
                                </Button>

                                {/* Trust indicator */}
                                <div style={{ textAlign: "center" }}>
                                    <Space size={6}>
                                        <SafetyCertificateOutlined
                                            style={{
                                                fontSize: 13,
                                                color: token.colorSuccess,
                                            }}
                                        />
                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 12 }}
                                        >
                                            Secured by Razorpay · SSL encrypted
                                        </Text>
                                    </Space>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                </Row>

                <PricingModal
                    open={changePlanOpen}
                    onClose={() => setChangePlanOpen(false)}
                    onSubscribe={(newPlan) => {
                        setCurrentPlan(newPlan);
                        onChangePlan?.(newPlan, "subscription");
                        setChangePlanOpen(false);
                    }}
                    onStartTrial={(newPlan) => {
                        setCurrentPlan(newPlan);
                        onChangePlan?.(newPlan, "trial");
                        setChangePlanOpen(false);
                    }}
                    onOneTimePurchase={(newPlan) => {
                        setCurrentPlan(newPlan);
                        onChangePlan?.(newPlan, "one_time");
                        setChangePlanOpen(false);
                    }}
                    loading={false}
                />
            </div>
        </QurealSection>
    );
};

export default BillingReviewSection;
